import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ADMIN_UNAUTHORIZED_EVENT, apiClient } from '../services/apiClient';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshAdmin = async () => {
    try {
      setAdminUser(await apiClient.getAdminMe());
    } catch {
      setAdminUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAdmin();
  }, []);

  useEffect(() => {
    const syncAuthState = () => {
      refreshAdmin().catch(() => {});
    };

    const interval = setInterval(syncAuthState, 30000);
    window.addEventListener('focus', syncAuthState);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', syncAuthState);
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setAdminUser(null);
      setLoading(false);
    };

    window.addEventListener(ADMIN_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(ADMIN_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const login = async (username, password) => {
    const me = await apiClient.adminLogin(username, password);
    setAdminUser(me);
    return me;
  };

  const logout = async () => {
    await apiClient.adminLogout();
    setAdminUser(null);
  };

  const value = useMemo(() => ({ adminUser, loading, login, logout, refreshAdmin, isAuthenticated: !!adminUser }), [adminUser, loading]);
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return context;
}
